import React, { Component, ReactNode } from 'react';
import { Box, Typography, Button, Alert } from '@mui/material';
import { AuthError } from '../utils/getAuthHeader';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
  isAuthError: boolean;
}

export class AuthErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, error: null, isAuthError: false };
  }

  static getDerivedStateFromError(error: Error): State {
    const isAuthError = error instanceof AuthError;
    return {
      hasError: true,
      error,
      isAuthError,
    };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error('AuthErrorBoundary caught an error:', error, errorInfo);

    // If it's an auth error, we might want to trigger a logout
    if (error instanceof AuthError) {
      console.error('Authentication error caught by boundary:', error.message);
    }
  }

  handleRetry = () => {
    this.setState({ hasError: false, error: null, isAuthError: false });
  };

  handleReload = () => {
    window.location.reload();
  };

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }

      const errorMessage = this.state.isAuthError
        ? 'Authentication error occurred. Please log in again.'
        : 'Something went wrong. Please try again.';

      return (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            height: '100vh',
            padding: 3,
            gap: 2,
          }}
        >
          <Alert severity={this.state.isAuthError ? 'warning' : 'error'} sx={{ mb: 2 }}>
            <Typography variant="h6" gutterBottom>
              {this.state.isAuthError ? 'Authentication Error' : 'Application Error'}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {errorMessage}
            </Typography>
          </Alert>

          <Box sx={{ display: 'flex', gap: 2 }}>
            <Button variant="contained" onClick={this.handleRetry}>
              Try Again
            </Button>
            <Button variant="outlined" onClick={this.handleReload}>
              Reload Page
            </Button>
          </Box>
        </Box>
      );
    }

    return this.props.children;
  }
}
